/**
 * AiCategorizeDemo — landing-page animation that visualizes the AI
 * auto-categorization step. A live transactions table loads with empty
 * Category cells, a Claude/AI badge pulses, and category chips fly from
 * the badge into each row in sequence with a confidence %.
 *
 * Auto-loops every ~9s. Pure CSS keyframes, no external libs.
 */
import { useEffect, useState } from "react";
import { Sparkles, BrainCircuit, RotateCcw } from "lucide-react";

// Fixed mock transactions — order matters for the cascade animation.
const TXNS = [
  { date: "01/03/2024", desc: "STRIPE PAYOUT · 0024-FX",   amount: "+$3,420.55", cat: "Sales Revenue",     conf: 98, tone: "income"  },
  { date: "01/05/2024", desc: "OFFICE RENT · ACME PROP",   amount: "−$1,800.00", cat: "Rent",              conf: 97, tone: "expense" },
  { date: "01/07/2024", desc: "GUSTO · PAYROLL JAN 1H",    amount: "−$6,200.00", cat: "Payroll",           conf: 99, tone: "expense" },
  { date: "01/09/2024", desc: "ANTHROPIC · API USAGE",     amount: "−$214.40",   cat: "Software · SaaS",   conf: 94, tone: "expense" },
  { date: "01/12/2024", desc: "ACH DEPOSIT · CLIENT A",    amount: "+$2,500.00", cat: "Sales Revenue",     conf: 96, tone: "income"  },
];

// Each row gets categorized at this offset (ms from animation start).
const REVEAL_AT = [600, 1500, 2400, 3300, 4200];
const TOTAL_MS = 7800;  // full loop length

const TONE_BG = {
  income:  "rgba(40, 167, 69, 0.10)",
  expense: "rgba(220, 53, 69, 0.08)",
};
const TONE_FG = {
  income:  "var(--ll-success)",
  expense: "var(--ll-error)",
};

export default function AiCategorizeDemo() {
  const [tick, setTick] = useState(0);          // elapsed ms in this loop
  const [paused, setPaused] = useState(false);
  const [loopKey, setLoopKey] = useState(0);    // bumps to restart CSS animations

  useEffect(() => {
    if (paused) return;
    const start = Date.now();
    const id = setInterval(() => {
      const t = Date.now() - start;
      if (t >= TOTAL_MS) {
        setTick(0);
        setLoopKey((k) => k + 1);
        return;
      }
      setTick(t);
    }, 80);
    return () => clearInterval(id);
  }, [paused, loopKey]);

  const replay = () => {
    setPaused(false);
    setTick(0);
    setLoopKey((k) => k + 1);
  };

  const revealed = TXNS.map((_, i) => tick >= REVEAL_AT[i]);
  const revealedCount = revealed.filter(Boolean).length;
  const thinking = revealedCount < TXNS.length && tick > 200;

  return (
    <section
      className="border-b py-20"
      style={{
        borderColor: "var(--ll-border)",
        background: "linear-gradient(180deg, var(--ll-bg-card) 0%, #FFFFFF 100%)",
      }}
      data-testid="ai-categorize-demo-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Stage column (left, flipped from preview demo for variety) */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div
              className="bg-white border shadow-xl overflow-hidden relative"
              style={{ borderColor: "var(--ll-border)", aspectRatio: "16 / 11" }}
              data-testid="ai-demo-stage"
            >
              {/* Browser chrome */}
              <div className="flex items-center gap-1.5 px-3 py-2 border-b bg-[var(--ll-bg-sidebar)]" style={{ borderColor: "var(--ll-border)" }}>
                <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                <div className="ml-3 text-[10px] font-mono-fin tracking-widest text-[var(--ll-ink-soft)] truncate">
                  financialsready.app/app/transactions
                </div>
                {/* AI badge — anchor for the flying chips */}
                <div
                  className="ml-auto flex items-center gap-1.5 px-2 py-1 border bg-white"
                  style={{
                    borderColor: thinking ? "var(--ll-ink)" : "var(--ll-border)",
                    boxShadow: thinking ? "0 0 0 0 rgba(27, 67, 50, 0.4)" : "none",
                    animation: thinking ? "ai-badge-pulse 1.4s ease-out infinite" : "none",
                    transition: "border-color 0.3s",
                  }}
                >
                  <BrainCircuit
                    size={12}
                    className="text-[var(--ll-ink)]"
                    style={{
                      animation: thinking ? "ai-badge-spin 2.4s linear infinite" : "none",
                    }}
                  />
                  <span className="text-[9px] uppercase tracking-widest text-[var(--ll-ink)] font-medium">
                    {thinking ? "Claude · thinking" : "Claude · idle"}
                  </span>
                </div>
              </div>

              {/* Header strip */}
              <div className="px-5 py-4 border-b" style={{ borderColor: "var(--ll-border)" }}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">January 2024</div>
                <div className="flex items-baseline justify-between">
                  <div className="font-display font-bold text-base mt-0.5">Transactions</div>
                  <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-soft)] font-mono-fin">
                    <span className="text-[var(--ll-ink)] font-medium">{revealedCount}</span> / {TXNS.length} categorized
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-hidden" key={loopKey}>
                <table className="w-full text-[10px]">
                  <thead>
                    <tr className="bg-[var(--ll-bg-sidebar)] border-b" style={{ borderColor: "var(--ll-border)" }}>
                      <th className="px-3 py-1.5 text-left uppercase tracking-widest font-medium text-[9px]">Date</th>
                      <th className="px-3 py-1.5 text-left uppercase tracking-widest font-medium text-[9px]">Description</th>
                      <th className="px-3 py-1.5 text-right uppercase tracking-widest font-medium text-[9px]">Amount</th>
                      <th className="px-3 py-1.5 text-left uppercase tracking-widest font-medium text-[9px] w-[42%]">Category</th>
                    </tr>
                  </thead>
                  <tbody>
                    {TXNS.map((t, i) => (
                      <tr key={i} className="border-b" style={{ borderColor: "var(--ll-border)" }}>
                        <td className="px-3 py-2 font-mono-fin whitespace-nowrap">{t.date}</td>
                        <td className="px-3 py-2 font-mono-fin whitespace-nowrap text-[var(--ll-ink)]">{t.desc}</td>
                        <td
                          className="px-3 py-2 font-mono-fin whitespace-nowrap text-right"
                          style={{ color: t.amount.startsWith("−") ? "var(--ll-error)" : "var(--ll-success)" }}
                        >
                          {t.amount}
                        </td>
                        <td className="px-3 py-2">
                          {/* Empty placeholder until reveal */}
                          {!revealed[i] && (
                            <span className="text-[var(--ll-ink-muted)] italic font-mono-fin text-[10px]">
                              {tick > REVEAL_AT[i] - 350 ? (
                                <span className="inline-flex items-center gap-1">
                                  <span className="inline-block w-1 h-1 rounded-full bg-[var(--ll-ink-soft)] animate-pulse" />
                                  <span className="inline-block w-1 h-1 rounded-full bg-[var(--ll-ink-soft)] animate-pulse" style={{ animationDelay: "0.15s" }} />
                                  <span className="inline-block w-1 h-1 rounded-full bg-[var(--ll-ink-soft)] animate-pulse" style={{ animationDelay: "0.3s" }} />
                                </span>
                              ) : "—"}
                            </span>
                          )}
                          {revealed[i] && (
                            <span
                              className="inline-flex items-center gap-1.5 px-2 py-0.5 border font-mono-fin text-[10px]"
                              style={{
                                borderColor: TONE_FG[t.tone],
                                background: TONE_BG[t.tone],
                                color: TONE_FG[t.tone],
                                animation: `ai-chip-fly 0.55s cubic-bezier(0.22, 1, 0.36, 1) both`,
                              }}
                              data-testid={`ai-demo-chip-${i}`}
                            >
                              <Sparkles size={10} />
                              {t.cat}
                              <span className="text-[8px] opacity-80 uppercase tracking-widest">{t.conf}%</span>
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Footer progress */}
              <div className="px-5 py-3 border-t flex items-center gap-3 text-[10px] uppercase tracking-widest text-[var(--ll-ink-soft)]" style={{ borderColor: "var(--ll-border)" }}>
                <div className="flex-1 h-[3px] bg-[var(--ll-border)] overflow-hidden">
                  <div
                    className="h-full transition-all duration-100"
                    style={{
                      width: `${Math.min(100, (revealedCount / TXNS.length) * 100)}%`,
                      background: "var(--ll-ink)",
                    }}
                  />
                </div>
                <span className="font-mono-fin">
                  {revealedCount === TXNS.length ? "100% · ready to export" : `${Math.round((revealedCount / TXNS.length) * 100)}%`}
                </span>
              </div>
            </div>

            {/* Caption */}
            <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-soft)] text-center font-mono-fin">
              Animated mockup · live AI runs server-side via Claude Sonnet 4.5
            </div>
          </div>

          {/* Copy column */}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="text-xs uppercase tracking-[0.25em] text-[var(--ll-ink-soft)] mb-3 flex items-center gap-2">
              <BrainCircuit size={12} /> Claude Sonnet 4.5 · AI categorization
            </div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              Every row gets a category.
              <br />
              <span className="italic text-[var(--ll-ochre)]">Without</span> the rule-builder.
            </h2>
            <p className="mt-5 text-base text-black/80 max-w-md">
              Financials Ready reads your transaction descriptions the way an accountant would — recognizing payouts, payroll runs, SaaS subscriptions, and rent without you teaching it a single rule. You see a confidence score on every guess, and one-click override fixes anything Claude misreads.
            </p>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <Stat label="Avg accuracy"   value="96%" />
              <Stat label="Rules required" value="0" />
              <Stat label="Categories"     value="40+" />
              <Stat label="Re-train cost"  value="$0" />
            </div>

            <div className="mt-6 flex items-center gap-3">
              <button
                onClick={replay}
                data-testid="ai-demo-replay"
                className="text-xs uppercase tracking-widest px-3 py-2 border bg-white hover:bg-[var(--ll-mint)] flex items-center gap-1.5"
                style={{ borderColor: "var(--ll-border)" }}
              >
                <RotateCcw size={12} /> Replay
              </button>
              <button
                onClick={() => setPaused((p) => !p)}
                data-testid="ai-demo-pause"
                className="text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] hover:text-[var(--ll-ink)]"
              >
                {paused ? "Resume" : "Pause"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ai-chip-fly {
          0%   { transform: translate(40px, -28px) scale(0.6); opacity: 0; filter: blur(1px); }
          55%  { transform: translate(0, 2px)      scale(1.06); opacity: 1; filter: blur(0);   }
          100% { transform: translate(0, 0)        scale(1);    opacity: 1; }
        }
        @keyframes ai-badge-pulse {
          0%   { box-shadow: 0 0 0 0    rgba(27, 67, 50, 0.5); }
          70%  { box-shadow: 0 0 0 10px rgba(27, 67, 50, 0);   }
          100% { box-shadow: 0 0 0 0    rgba(27, 67, 50, 0);   }
        }
        @keyframes ai-badge-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border p-3" style={{ borderColor: "var(--ll-border)", background: "var(--ll-bg-card)" }}>
      <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">{label}</div>
      <div className="font-display font-bold text-xl text-[var(--ll-ink)] mt-0.5">{value}</div>
    </div>
  );
}
